import { type FC, type ReactElement, useState } from 'react'
import { useNavigate } from 'react-router'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	Dialog,
	DialogBody,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'
import { useApproveFinalReviewListing } from '../queries/use-approve-final-review-listing'
import { useLuxeFeatureFlag } from '../queries/use-get-luxe-feature-flags'

interface FinalReviewApprovalDialogProps {
	listingId: string
	trigger: ReactElement
	isEscalated?: boolean
}

export const FinalReviewApprovalDialog: FC<FinalReviewApprovalDialogProps> = ({
	listingId,
	trigger,
	isEscalated = false
}) => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [open, setOpen] = useState(false)
	const { isEnabled: isPostEscalationRedirectEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-post-escalation-redirect'
	)
	const approveMutation = useApproveFinalReviewListing(listingId, {
		onSuccess: () => {
			setOpen(false)

			// Resolving an escalation returns the reviewer to the Final Review
			// queue to keep working through the escalated listings, rather than
			// dropping them into Quote Generation.
			navigate(
				isPostEscalationRedirectEnabled && isEscalated
					? routes.LUXE_SUBMISSIONS_FINAL_REVIEW.handle.fullPath
					: routes.LUXE_SUBMISSIONS_QUOTE_GENERATION.handle.fullPath
			)
		}
	})

	return (
		<Dialog onOpenChange={setOpen} open={open}>
			<DialogTrigger render={trigger} />
			<DialogContent className='max-w-120' showCloseButton={false}>
				<DialogHeader>
					{t('luxe_submissions:listing_approval')}
				</DialogHeader>

				<DialogBody>
					<DialogTitle>
						{t('luxe_submissions:approve_listing_question')}
					</DialogTitle>
					<DialogDescription className='flex flex-col gap-2'>
						{t('luxe_submissions:approve_listing_description')}

						<span className='flex justify-between rounded-lg bg-grey-100 p-4'>
							<span className='text-neutral-900 text-xs'>
								{' '}
								{t('luxe_submissions:listing', {
									count: 1
								}).toLocaleUpperCase()}
							</span>
							<span className='font-semibold text-md text-neutral-900'>
								#{listingId}
							</span>
						</span>
					</DialogDescription>
				</DialogBody>

				<DialogFooter className='flex flex-row justify-between bg-neutral-white'>
					<DialogClose
						render={
							<ButtonLegacy type='button' variant='tertiary' />
						}
					>
						{t('common:close')}
					</DialogClose>
					<ButtonLegacy
						disabled={approveMutation.isPending}
						onClick={() => approveMutation.mutate()}
						type='button'
						variant='brand'
					>
						{t('luxe_submissions:approve_listing')}
						{approveMutation.isPending && <Spinner />}
					</ButtonLegacy>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
